<template>
  <div class="min-h-screen bg-gray-50 font-poppins text-gray-900">
    <!-- Slim account header: menu (desktop: shop links) | logo, centered | cart -->
    <header class="sticky top-0 z-40 border-b border-gray-200 bg-white">
      <div class="mx-auto grid h-14 max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-4 px-4 lg:h-16 lg:px-6">
        <div class="flex items-center">
          <button @click="isMenuOpen = !isMenuOpen" :aria-expanded="isMenuOpen" aria-label="Menu"
            class="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-brand-800 hover:bg-brand-50 lg:hidden">
            <X v-if="isMenuOpen" class="h-5 w-5" />
            <Menu v-else class="h-5 w-5" />
          </button>
          <nav class="-ml-3 hidden items-center gap-1 lg:flex">
            <router-link v-for="link in shopLinks" :key="link.to" :to="link.to"
              class="rounded-full px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-brand-50 hover:text-brand-900">
              {{ link.label }}
            </router-link>
          </nav>
        </div>

        <router-link to="/" class="flex items-center">
          <!-- The wide wordmark of the shop's mobile navbar -->
          <img src="@brand/assets/delikat-navbar-mobile.webp" :alt="$brand.siteName" width="2386" height="1193" class="h-12 w-auto lg:h-14" />
        </router-link>

        <router-link v-if="!shopClosed" to="/checkout/cart" aria-label="Košík"
          class="relative -mr-2 flex h-9 w-9 items-center justify-center justify-self-end rounded-full text-brand-800 hover:bg-brand-50">
          <ShoppingCart class="h-5 w-5" />
          <span v-if="cartStore.totalQuantity > 0"
            class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {{ cartStore.totalQuantity > 99 ? '99+' : cartStore.totalQuantity }}
          </span>
        </router-link>
      </div>

      <!-- Mobile menu: shop links + account actions -->
      <div v-if="isMenuOpen" class="absolute inset-x-0 top-full border-b border-gray-200 bg-white lg:hidden">
        <nav class="mx-auto max-w-6xl px-2 py-2">
          <router-link v-for="link in shopLinks" :key="link.to" :to="link.to"
            class="flex h-11 items-center rounded-lg px-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {{ link.label }}
          </router-link>
          <div v-if="shopLinks.length" class="my-2 border-t border-gray-100"></div>
          <router-link v-if="authStore.isAdmin" to="/admin"
            class="flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Shield class="h-4 w-4 text-gray-400" />
            Admin panel
          </router-link>
          <button @click="logout"
            class="flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-red-700 hover:bg-red-50">
            <LogOut class="h-4 w-4" />
            Odhlásiť sa
          </button>
        </nav>
      </div>
    </header>

    <!-- Closes the mobile menu on outside tap -->
    <div v-if="isMenuOpen" class="fixed inset-0 z-30 bg-brand-950/30 lg:hidden" @click="isMenuOpen = false"></div>

    <div class="mx-auto flex max-w-6xl gap-10 px-4 lg:px-6">
      <!-- Desktop sidebar (part of the page, not floating) -->
      <aside class="hidden w-56 shrink-0 lg:block">
        <div class="sticky top-16 py-8">
          <div class="mb-6 flex items-center gap-3">
            <router-link v-if="hasFeature('avatars')" to="/user/settings" aria-label="Zmeniť profilovú fotku" class="shrink-0">
              <UserAvatar :user="authStore.user" class="h-12 w-12 text-sm ring-2 ring-brand-100" />
            </router-link>
            <div v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-800 text-sm font-semibold text-white">
              {{ initials }}
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold">{{ authStore.displayName }}</p>
              <p class="truncate text-xs text-gray-500">{{ authStore.user?.email }}</p>
            </div>
          </div>

          <nav class="space-y-0.5">
            <router-link v-for="item in navigationItems" :key="item.to" :to="item.to"
              class="relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              :class="isActive(item) ? 'bg-white text-brand-900 ring-1 ring-gray-200' : 'text-gray-600 hover:bg-white hover:text-brand-900'">
              <span v-if="isActive(item)" class="absolute -left-px top-2 bottom-2 w-0.5 rounded-full bg-red-600"></span>
              <component :is="item.icon" class="h-4 w-4 shrink-0" :class="isActive(item) ? 'text-brand-800' : 'text-gray-400'" />
              {{ item.name }}
            </router-link>
          </nav>

          <div class="mt-6 space-y-0.5 border-t border-gray-200 pt-6">
            <router-link v-if="authStore.isAdmin" to="/admin"
              class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white hover:text-brand-900">
              <Shield class="h-4 w-4 text-gray-400" />
              Admin panel
            </router-link>
            <button @click="logout"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white hover:text-red-700">
              <LogOut class="h-4 w-4 text-gray-400" />
              Odhlásiť sa
            </button>
          </div>
        </div>
      </aside>

      <!-- Page -->
      <main class="min-w-0 flex-1 pt-5 pb-28 lg:py-8">
        <router-view />
      </main>
    </div>

    <!-- Mobile tab bar -->
    <nav class="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden">
      <div class="flex">
        <router-link v-for="item in navigationItems" :key="item.to" :to="item.to"
          class="relative flex min-w-0 flex-1 flex-col items-center gap-1 pt-2.5 pb-2 text-[11px] font-medium transition-colors"
          :class="isActive(item) ? 'text-brand-900' : 'text-gray-500'">
          <span v-if="isActive(item)" class="absolute top-0 h-0.5 w-8 rounded-full bg-red-600"></span>
          <component :is="item.icon" class="h-5 w-5" :class="isActive(item) ? 'text-brand-800' : 'text-gray-400'" />
          <span class="max-w-full truncate px-1">{{ item.short || item.name }}</span>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useCartStore } from '@/stores/cart'
import { useSettingsStore } from '@/stores/settings'
import { hasFeature, pagePath } from '@/brand'
import UserAvatar from '@/components/user/UserAvatar.vue'
import {
  LayoutDashboard,
  PackageOpen,
  MapPin,
  Settings,
  Phone,
  Plus,
  Gift,
  ShoppingCart,
  LogOut,
  Shield,
  Menu,
  X,
  MessagesSquare
} from 'lucide-vue-next'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()

// Maintenance: customers only have their account, the shop links and the cart would bounce to the maintenance page
const shopClosed = computed(() => settingsStore.maintenanceMode && !authStore.isAdmin)

const shopLinks = computed(() => shopClosed.value ? [] : [
  { label: 'Domov', to: '/' },
  { label: 'Produkty', to: '/products' },
  { label: 'Kategórie', to: '/categories' },
  { label: 'O nás', to: pagePath('about') },
  { label: 'Kontakt', to: pagePath('contact') }
])

const isMenuOpen = ref(false)
watch(() => route.fullPath, () => { isMenuOpen.value = false })

const navigationItems = computed(() => {
  const items = [
    { name: 'Prehľad', to: '/user', icon: LayoutDashboard, exact: true },
    { name: 'Objednávky', to: '/user/orders', icon: PackageOpen },
    { name: 'Správy', to: '/user/messages', icon: MessagesSquare, feature: 'chat' },
    { name: 'Odmeny', to: '/user/rewards', icon: Gift, feature: 'rewards' },
    { name: 'Adresy', to: '/user/addresses', icon: MapPin },
    { name: 'Nastavenia', to: '/user/settings', icon: Settings, short: 'Účet' }
  ]

  if ((authStore.isAdmin || authStore.isSalesman) && hasFeature('telemarketing')) {
    items.splice(-1, 0, { name: 'Telemarketing', to: '/user/marketing', icon: Phone })
  }

  if (authStore.isAdmin) {
    items.splice(-1, 0, { name: 'Vytvoriť objednávku', short: 'Nová', to: '/user/create-order', icon: Plus })
  }

  return items.filter(item => !item.feature || hasFeature(item.feature))
})

// Order detail (/user/orders/123) keeps "Objednávky" active
const isActive = (item) => (item.exact ? route.path === item.to : route.path.startsWith(item.to))

const initials = computed(() => {
  const user = authStore.user
  const letters = [user?.first_name, user?.last_name].filter(Boolean).map(name => name[0])
  return (letters.join('') || authStore.displayName?.[0] || 'U').toUpperCase()
})

const logout = async () => {
  await authStore.logout()
  router.push('/')
}

// Logins from before profile pictures existed have no avatar in the stored user
onMounted(() => {
  if (hasFeature('avatars') && authStore.user && !('avatar' in authStore.user)) {
    authStore.refreshUser().catch(() => {})
  }
})
</script>
